import type { BudgetEntryDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useEffect, useMemo, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { ApiError } from "../../lib/api-client"
import { BudgetEntryFormModal } from "../components/budget/budget-entry-form-modal"
import { BudgetEntryList } from "../components/budget/budget-entry-list"
import { CaptureInvoiceModal } from "../components/budget/capture-invoice-modal"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useBudgetEntries } from "../hooks/use-budget"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProjects } from "../hooks/use-projects"
import { useProperty } from "../hooks/use-property"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function PlusIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

/**
 * Property Budget tab (ZPL-224) — OWNER-ONLY. Non-owners never see the tab;
 * a direct URL hits the API's 404 and falls back to the dashboard, so budget
 * existence is never leaked.
 */
export function PropertyBudgetPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const { data: projects } = useProjects(id)
	const { setLastPropertyId } = useLastPropertyId()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tb = t.projects.budget

	const [shareOpen, setShareOpen] = useState(false)
	const [formOpen, setFormOpen] = useState(false)
	const [captureOpen, setCaptureOpen] = useState(false)
	const [editingEntry, setEditingEntry] = useState<BudgetEntryDTO | null>(null)

	const budget = useBudgetEntries(id ?? "")

	const budgetNotFound =
		budget.error instanceof ApiError && budget.error.status === 404
	const isNotFound =
		!id || (error instanceof ApiError && error.status === 404) || budgetNotFound

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (isNotFound) setLastPropertyId(null)
	}, [property, isNotFound, setLastPropertyId])

	const entries = budget.data?.entries ?? []

	const projectNameById = useMemo(
		() => new Map((projects ?? []).map((p) => [p.id, p.name])),
		[projects],
	)

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading || budget.isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error || (budget.error && !budgetNotFound)) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{error instanceof ApiError ? error.message : tb.errorLoad}
			</div>
		)
	}
	if (!property || !budget.data) return null

	const { summary } = budget.data

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<PageHeader
					eyebrow={property.name}
					eyebrowHref={webPaths.property(property.id)}
					title={tb.title}
					meta={
						<>
							<div className="md:text-right">
								<div>
									<span className="text-sm text-muted-foreground mr-2">
										{tb.spentLabel}
									</span>
									<span className="text-2xl font-bold text-foreground">
										{summary.spentConfirmed} {summary.contextCurrency}
									</span>
								</div>
								{summary.excludedFromSum > 0 ? (
									<div className="text-xs text-warning">
										{tb.excludedNote.replace(
											"{count}",
											String(summary.excludedFromSum),
										)}
									</div>
								) : null}
								{summary.draftCount > 0 ? (
									<div className="text-xs text-muted-foreground">
										{summary.draftCount} {tb.draftsLabel}
									</div>
								) : null}
							</div>
							{entries.length > 0 ? (
								<div className="flex items-center gap-4">
									<button
										type="button"
										onClick={() => setCaptureOpen(true)}
										className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
									>
										{tb.captureCta}
									</button>
									<button
										type="button"
										onClick={() => setFormOpen(true)}
										className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
									>
										<PlusIcon />
										{tb.addCta}
									</button>
								</div>
							) : null}
						</>
					}
				/>

				{entries.length === 0 ? (
					<div className="rounded-xl border border-dashed border-border px-6 py-14 flex flex-col items-center text-center gap-3">
						<h2 className="text-lg font-semibold text-foreground">
							{tb.emptyTitle}
						</h2>
						<p className="text-sm text-muted-foreground max-w-md">
							{tb.emptyBody}
						</p>
						<div className="flex items-center gap-3 mt-2">
							<button
								type="button"
								onClick={() => setFormOpen(true)}
								className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
							>
								<PlusIcon />
								{tb.addCta}
							</button>
							<button
								type="button"
								onClick={() => setCaptureOpen(true)}
								className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors"
							>
								{tb.captureCta}
							</button>
						</div>
					</div>
				) : (
					<BudgetEntryList
						propertyId={property.id}
						entries={entries}
						projectNameById={projectNameById}
						onEdit={(entry) => setEditingEntry(entry)}
					/>
				)}
			</PageContainer>

			<PropertyBottomNav propertyId={property.id} />
			<PropertyChat propertyId={property.id} />

			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			{formOpen ? (
				<BudgetEntryFormModal
					propertyId={property.id}
					entry={null}
					projects={projects ?? []}
					onClose={() => setFormOpen(false)}
				/>
			) : null}
			{editingEntry ? (
				<BudgetEntryFormModal
					// Remount per entry so the form state resets.
					key={editingEntry.id}
					propertyId={property.id}
					entry={editingEntry}
					projects={projects ?? []}
					onClose={() => setEditingEntry(null)}
				/>
			) : null}
			{captureOpen ? (
				<CaptureInvoiceModal
					propertyId={property.id}
					projects={projects ?? []}
					onClose={() => setCaptureOpen(false)}
				/>
			) : null}
		</div>
	)
}
